import React, { useEffect, useState, useCallback } from "react";
import api from "../api/axiosInstance";
import { FaChartBar, FaFilePdf, FaFilter } from "react-icons/fa";
import { useLocation } from "react-router-dom";

const ViewPassbook = () => {
  const location = useLocation();
  const passedState = location.state || {};
  
  console.log("ViewPassbook - passedState:", passedState);
  console.log("ViewPassbook - has passbookData:", !!passedState.passbookData);
  
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showFilters, setShowFilters] = useState(true);

  const [filters, setFilters] = useState({
    name: passedState.customerName || "",
    itemName: passedState.productName || "",
    fromDate: "",
    toDate: "",
    site: "",
    status: ""
  });

  const [summary, setSummary] = useState({
    totalDelivered: 0,
    totalReceived: 0,
    totalBalance: 0
  });

  /* ---------------- FETCH PASSBOOK ---------------- */
  const fetchPassbook = useCallback(async () => {
    // Check if we have data from navigation state
    if (passedState.passbookData) {
      console.log("Using passed state data:", passedState.passbookData);
      
      setLoading(true);
      setError("");
      
      try {
        const data = passedState.passbookData.content || [];
        console.log("Setting records from passed data:", data);
        setRecords(data);
        setTotalPages(passedState.passbookData.totalPages || 0);

        // Calculate summary
        const delivered = data.reduce((sum, item) => {
          const itemDelivered = item.items?.reduce((itemSum, itemDetail) => 
            itemSum + (parseFloat(itemDetail.deliveredQty) || 0), 0) || 0;
          return sum + itemDelivered;
        }, 0);
        
        const received = data.reduce((sum, item) => {
          const itemReceived = item.items?.reduce((itemSum, itemDetail) => 
            itemSum + (parseFloat(itemDetail.receivedQty) || 0), 0) || 0;
          return sum + itemReceived;
        }, 0);
        
        const balance = data.length > 0 ? 
          data[data.length - 1].items?.[0]?.closingBalance || 0 : 0;
        
        console.log("Calculated summary:", { delivered, received, balance });
        
        setSummary({
          totalDelivered: delivered,
          totalReceived: received,
          totalBalance: balance
        });

        console.log("Summary set successfully");
        
      } catch (err) {
        console.error("Error processing passed data:", err);
        setError("Failed to process passbook data");
      } finally {
        setLoading(false);
      }
      return;
    }

    // Fallback to API call if no passed data
    console.log("No passed data, making API call");
    
    if (filters.fromDate && filters.toDate && filters.fromDate > filters.toDate) {
      setError("From date cannot be greater than To date");
      return;
    }

    try {
      setLoading(true);
      setError("");

      console.log("Fetching passbook with filters:", filters);
      console.log("Request params:", { ...filters, page, size: 10 });

      const response = await api.get("challans/passbook", {
        params: {
          ...filters,
          page,
          size: 10
        }
      });

      console.log("API Response:", response);
      console.log("Response data:", response.data);
      console.log("Response content:", response.data?.content);

      const data = response.data.content || [];
      setRecords(data);
      setTotalPages(response.data.totalPages || 0);

      // Calculate summary
      const delivered = data.reduce((sum, item) => {
        const itemDelivered = item.items?.reduce((itemSum, itemDetail) => 
          itemSum + (parseFloat(itemDetail.deliveredQty) || 0), 0) || 0;
        return sum + itemDelivered;
      }, 0);
      
      const received = data.reduce((sum, item) => {
        const itemReceived = item.items?.reduce((itemSum, itemDetail) => 
          itemSum + (parseFloat(itemDetail.receivedQty) || 0), 0) || 0;
        return sum + itemReceived;
      }, 0);
      
      const balance = data.length > 0 ? 
        data[data.length - 1].items?.[0]?.closingBalance || 0 : 0;
      
      setSummary({
        totalDelivered: delivered,
        totalReceived: received,
        totalBalance: balance
      });

      console.log("Summary calculated:", { delivered, received, balance });

    } catch (err) {
      console.error("Error fetching passbook:", err);
      console.error("Error details:", err.response);
      setError(err.response?.data?.message || "Failed to fetch passbook data");
    } finally {
      setLoading(false);
    }
  }, [filters, page, passedState]);

  useEffect(() => {
    console.log("ViewPassbook useEffect triggered");
    console.log("passedState:", passedState);
    console.log("passedState.passbookData:", passedState.passbookData);
    
    // If we have passed data, use it immediately without API calls
    if (passedState && passedState.passbookData) {
      console.log("Initializing with passed data");
      const data = passedState.passbookData.content || [];
      console.log("Data to set:", data);
      setRecords(data);
      setTotalPages(passedState.passbookData.totalPages || 0);

      // Calculate summary
      const delivered = data.reduce((sum, item) => {
        const itemDelivered = item.items?.reduce((itemSum, itemDetail) => 
          itemSum + (parseFloat(itemDetail.deliveredQty) || 0), 0) || 0;
        return sum + itemDelivered;
      }, 0);
      
      const received = data.reduce((sum, item) => {
        const itemReceived = item.items?.reduce((itemSum, itemDetail) => 
          itemSum + (parseFloat(itemDetail.receivedQty) || 0), 0) || 0;
        return sum + itemReceived;
      }, 0);
      
      const balance = data.length > 0 ? 
        data[data.length - 1].items?.[0]?.closingBalance || 0 : 0;
      
      setSummary({
        totalDelivered: delivered,
        totalReceived: received,
        totalBalance: balance
      });

      console.log("Initialized with passed data:", { 
        records: data.length, 
        delivered, 
        received, 
        balance 
      });
      return;
    }
    
    console.log("No passed data found, making API call");
    // Only make API call if no passed data
    fetchPassbook();
  }, [passedState]);

  /* ---------------- DOWNLOAD PDF ---------------- */
  const handleDownloadPDF = async (record) => {
    try {
      // Create PDF download URL - adjust based on your API
      const pdfUrl = `challans/download/${record.id}`;
      
      const response = await api.get(pdfUrl, {
        responseType: 'blob' // Important for file downloads
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `challan_${record.challanNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error("Error downloading PDF:", error);
      alert("Failed to download PDF. Please try again.");
    }
  };

  /* ---------------- HANDLERS ---------------- */
  const handleChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleReset = () => {
    setFilters({ 
      name: "", 
      itemName: "", 
      fromDate: "", 
      toDate: "",
      siteLocatio: "",
      status: ""
    });
    setPage(0);
  };

  const handleSearch = () => {
    setPage(0);
    // Trigger search with current filters
    fetchPassbook();
  };

  const handleExport = () => {
    if (records.length === 0) {
      alert("No data to export");
      return;
    }

    // Create CSV content
    const headers = ["Challan No.", "Date", "Sr No.", "PO No.", "SiteLocation", "Opening", "Delivered", "Received", "Balance"];
    const csvContent = [
      headers.join(","),
      ...records.map(row => [
        row.challanNumber || '',
        row.date || '',
        row.srNo || '',
        row.poNumber || '',
        row.siteLocation || '',
        row.openingBalance || '0',
        row.deliveredQty || '0',
        row.receivedQty || '0',
        row.balance || '0'
      ].join(","))
    ].join("\n");

    // Create download link
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `passbook_${passedState.customerName || 'unknown'}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div style={{ 
      padding: '20px',
      maxWidth: '100%',
      width: '100%',
      overflowX: 'hidden',
      minHeight: '100vh',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header with Summary */}
      <div style={{ 
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        padding: '20px',
        marginBottom: '20px',
        boxSizing: 'border-box'
      }}>
        <div style={{ 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '20px',
          flexWrap: 'wrap',
          marginBottom: '20px'
        }}>
          <div style={{ flex: '1', minWidth: '250px' }}>
            <h2 style={{ 
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              margin: '0 0 12px 0',
              fontSize: 'clamp(1.2rem, 4vw, 1.8rem)',
              fontWeight: 'bold',
              color: '#1e293b'
            }}>
              <FaChartBar style={{ 
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                color: '#3b82f6'
              }} />
              Passbook Details
            </h2>
            <div style={{ 
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              marginBottom: '8px'
            }}>
              {passedState.customerName && (
                <span style={{
                  display: 'inline-block',
                  padding: '4px 8px',
                  backgroundColor: '#f1f5f9',
                  color: '#475569',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: '500'
                }}>
                  Customer: {passedState.customerName}
                </span>
              )}
              {passedState.productName && (
                <span style={{
                  display: 'inline-block',
                  padding: '4px 8px',
                  backgroundColor: '#f1f5f9',
                  color: '#475569',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: '500'
                }}>
                  Product: {passedState.productName}
                </span>
              )}
            </div>
            <span style={{ 
              color: '#64748b',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              {records.length} Records
            </span>
          </div>
          <div style={{ 
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap',
            minWidth: '300px'
          }}>
            <div style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              padding: '12px 16px',
              textAlign: 'center',
              minWidth: '100px',
              flex: '1'
            }}>
              <div style={{ 
                fontSize: '12px',
                color: '#7f1d1d',
                fontWeight: '500',
                marginBottom: '4px'
              }}>
                Total Delivered
              </div>
              <div style={{ 
                fontSize: 'clamp(1rem, 3vw, 1.25rem)',
                color: '#dc2626',
                fontWeight: 'bold'
              }}>
                {(summary.totalDelivered || 0).toFixed(2)}
              </div>
            </div>
            <div style={{
              backgroundColor: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: '6px',
              padding: '12px 16px',
              textAlign: 'center',
              minWidth: '100px',
              flex: '1'
            }}>
              <div style={{ 
                fontSize: '12px',
                color: '#14532d',
                fontWeight: '500',
                marginBottom: '4px'
              }}>
                Total Received
              </div>
              <div style={{ 
                fontSize: 'clamp(1rem, 3vw, 1.25rem)',
                color: '#16a34a',
                fontWeight: 'bold'
              }}>
                {(summary.totalReceived || 0).toFixed(2)}
              </div>
            </div>
            <div style={{
              backgroundColor: summary.totalBalance < 0 ? '#fef2f2' : '#f0fdf4',
              border: summary.totalBalance < 0 ? '1px solid #fecaca' : '1px solid #bbf7d0',
              borderRadius: '6px',
              padding: '12px 16px',
              textAlign: 'center',
              minWidth: '100px',
              flex: '1'
            }}>
              <div style={{ 
                fontSize: '12px',
                color: summary.totalBalance < 0 ? '#7f1d1d' : '#14532d',
                fontWeight: '500',
                marginBottom: '4px'
              }}>
                Current Balance
              </div>
              <div style={{ 
                fontSize: 'clamp(1rem, 3vw, 1.25rem)',
                color: summary.totalBalance < 0 ? '#dc2626' : '#16a34a',
                fontWeight: 'bold'
              }}>
                {(summary.totalBalance || 0).toFixed(2)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div style={{ 
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          padding: '20px',
          marginBottom: '20px',
          boxSizing: 'border-box',
          overflow: 'hidden',
          width: '100%'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '12px',
            marginBottom: '16px'
          }}>
            <input 
              name="name" 
              placeholder="Customer Name" 
              value={filters.name} 
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: '#ffffff',
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'all 0.2s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#3b82f6';
                e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.boxShadow = 'none';
              }}
            />
            <input 
              name="itemName" 
              placeholder="Product Name" 
              value={filters.itemName} 
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: '#ffffff',
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'all 0.2s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#3b82f6';
                e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.boxShadow = 'none';
              }}
            />
            <input 
              type="date" 
              name="fromDate" 
              value={filters.fromDate} 
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: '#ffffff',
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'all 0.2s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#3b82f6';
                e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.boxShadow = 'none';
              }}
            />
            <input 
              type="date" 
              name="toDate" 
              value={filters.toDate} 
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: '#ffffff',
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'all 0.2s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#3b82f6';
                e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '12px',
            marginBottom: '16px'
          }}>
            <select 
              name="site" 
              value={filters.site} 
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: '#ffffff',
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'all 0.2s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#3b82f6';
                e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.boxShadow = 'none';
              }}
            >
              <option value="">All Sites</option>
              <option value="site1">Site 1</option>
              <option value="site2">Site 2</option>
              <option value="site3">Site 3</option>
              <option value="site4">Site 4</option>
            </select>
            
            <select 
              name="status" 
              value={filters.status} 
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: '#ffffff',
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'all 0.2s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#3b82f6';
                e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.boxShadow = 'none';
              }}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          
          <div style={{
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap'
          }}>
            <button 
              onClick={handleSearch}
              style={{
                padding: '10px 20px',
                border: '1px solid #3b82f6',
                borderRadius: '6px',
                backgroundColor: '#3b82f6',
                color: '#ffffff',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                textDecoration: 'none',
                boxShadow: '0 2px 4px rgba(59, 130, 246, 0.1)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#2563eb';
                e.target.style.borderColor = '#2563eb';
                e.target.style.boxShadow = '0 4px 8px rgba(59, 130, 246, 0.2)';
                e.target.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#3b82f6';
                e.target.style.borderColor = '#3b82f6';
                e.target.style.boxShadow = '0 2px 4px rgba(59, 130, 246, 0.1)';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              Search
            </button>
            <button 
              onClick={handleReset}
              style={{
                padding: '10px 20px',
                border: '1px solid #6b7280',
                borderRadius: '6px',
                backgroundColor: '#ffffff',
                color: '#6b7280',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                textDecoration: 'none',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f3f4f6';
                e.target.style.borderColor = '#374151';
                e.target.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#ffffff';
                e.target.style.borderColor = '#6b7280';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              Reset
            </button>
            <button 
              onClick={handleExport}
              title="Export to CSV"
              style={{
                padding: '10px 20px',
                border: '1px solid #16a34a',
                borderRadius: '6px',
                backgroundColor: '#16a34a',
                color: '#ffffff',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                textDecoration: 'none',
                boxShadow: '0 2px 4px rgba(22, 163, 74, 0.1)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#15803d';
                e.target.style.borderColor = '#15803d';
                e.target.style.boxShadow = '0 4px 8px rgba(22, 163, 74, 0.2)';
                e.target.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#16a34a';
                e.target.style.borderColor = '#16a34a';
                e.target.style.boxShadow = '0 2px 4px rgba(22, 163, 74, 0.1)';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              Export
            </button>
          </div>
        </div>
      )}

      {error && (
        <p style={{
          padding: '12px 16px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '6px',
          color: '#dc2626',
          fontSize: '14px',
          fontWeight: '500',
          marginBottom: '20px'
        }}>
          {error}
        </p>
      )}

      {/* Table */}
      <div style={{ 
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        overflow: 'hidden',
        width: '100%',
        maxWidth: '100vw',
        boxSizing: 'border-box',
        marginBottom: '20px',
        display: 'flex',
        flexDirection: 'column',
        margin: '0 auto'
      }}>
        <div style={{ 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          borderBottom: '1px solid #e2e8f0',
          backgroundColor: '#f8fafc',
          flexShrink: 0,
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <h3 style={{ 
            margin: 0,
            fontSize: 'clamp(1rem, 3vw, 1.25rem)',
            fontWeight: '600',
            color: '#1e293b'
          }}>
            Passbook Records
          </h3>
          <button 
            onClick={() => setShowFilters(!showFilters)}
            title="Toggle Filters"
            style={{
              padding: '8px 12px',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              backgroundColor: '#ffffff',
              color: '#64748b',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f8fafc';
              e.target.style.borderColor = '#3b82f6';
              e.target.style.color = '#3b82f6';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#ffffff';
              e.target.style.borderColor = '#e2e8f0';
              e.target.style.color = '#64748b';
            }}
          >
            <FaFilter style={{ fontSize: '14px' }} />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
        </div>
        
        <div className="responsive-table-container" style={{ 
          overflowX: 'auto',
          overflowY: 'hidden',
          width: '100%',
          maxWidth: '100%',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'thin',
          scrollbarColor: '#cbd5e1 #f1f5f9',
          flex: 1,
          position: 'relative',
          boxSizing: 'border-box'
        }}>
          {loading ? (
          <div className="loading-state" style={{ 
            padding: '60px 20px',
            textAlign: 'center',
            color: '#64748b',
            fontSize: '16px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px',
            minWidth: '800px'
          }}>
            <div style={{ fontSize: '48px' }}>Loading passbook...</div>
          </div>
        ) : (!records || records.length === 0) ? (
          <div className="no-data-state" style={{ 
            padding: '60px 20px',
            textAlign: 'center',
            color: '#64748b',
            fontSize: '16px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px',
            minWidth: '800px'
          }}>
            <div style={{ fontSize: '48px' }}>No records found</div>
            <div style={{ fontSize: '14px', color: '#9ca3af' }}>
              Try adjusting your filters or add new records
            </div>
          </div>
        ) : (
          <table className="responsive-table" style={{ 
              width: '100%',
              minWidth: '800px',
              borderCollapse: 'collapse',
              fontSize: 'clamp(0.65rem, 1.5vw, 0.75rem)',
              tableLayout: 'auto',
              backgroundColor: 'white'
            }}>
            <thead>
              <tr>
                <th style={{ 
                  padding: '8px 4px',
                  textAlign: 'center',
                  backgroundColor: '#f8fafc',
                  borderBottom: '1px solid #e2e8f0',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '0.65rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  width: '100px',
                  minWidth: '80px'
                }}>
                  Date
                </th>
                <th style={{ 
                  padding: '8px 4px',
                  textAlign: 'center',
                  backgroundColor: '#f8fafc',
                  borderBottom: '1px solid #e2e8f0',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '0.65rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  width: '120px',
                  minWidth: '100px'
                }}>
                  Challan No.
                </th>
                <th style={{ 
                  padding: '8px 4px',
                  textAlign: 'center',
                  backgroundColor: '#f8fafc',
                  borderBottom: '1px solid #e2e8f0',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '0.65rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  width: '60px',
                  minWidth: '50px'
                }}>
                  Type
                </th>
                <th style={{ 
                  padding: '8px 4px',
                  textAlign: 'center',
                  backgroundColor: '#f8fafc',
                  borderBottom: '1px solid #e2e8f0',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '0.65rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  width: '120px',
                  minWidth: '100px'
                }}>
                  Product
                </th>
                <th style={{ 
                  padding: '8px 4px',
                  textAlign: 'center',
                  backgroundColor: '#f8fafc',
                  borderBottom: '1px solid #e2e8f0',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '0.65rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  width: '70px',
                  minWidth: '60px'
                }}>
                  Quantity
                </th>
                <th style={{ 
                  padding: '8px 4px',
                  textAlign: 'center',
                  backgroundColor: '#f8fafc',
                  borderBottom: '1px solid #e2e8f0',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '0.65rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  width: '100px',
                  minWidth: '80px'
                }}>
                  Location
                </th>
                <th style={{ 
                  padding: '8px 4px',
                  textAlign: 'center',
                  backgroundColor: '#f8fafc',
                  borderBottom: '1px solid #e2e8f0',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '0.65rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  width: '80px',
                  minWidth: '70px'
                }}>
                  Amount
                </th>
                <th style={{ 
                  padding: '8px 4px',
                  textAlign: 'center',
                  backgroundColor: '#f8fafc',
                  borderBottom: '1px solid #e2e8f0',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '0.65rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  width: '80px',
                  minWidth: '70px'
                }}>
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {records.map((row, i) => {
                // Get the first item for display (or handle multiple items)
                const firstItem = row.items?.[0];
                const totalDelivered = row.items?.reduce((sum, item) => sum + (item.deliveredQty || 0), 0) || 0;
                const totalReceived = row.items?.reduce((sum, item) => sum + (item.receivedQty || 0), 0) || 0;
                const lastItemBalance = row.items?.[row.items?.length - 1]?.closingBalance || 0;
                
                return (
                  <tr key={i} style={{ 
                    borderBottom: '1px solid #f1f5f9',
                    transition: 'background-color 0.2s ease'
                  }}>
                    <td style={{ 
                      padding: '6px 4px',
                      color: '#64748b',
                      fontSize: '0.7rem',
                      textAlign: 'center',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {row.date || ''}
                    </td>
                    <td style={{ 
                      padding: '6px 4px',
                      color: '#1e293b',
                      fontWeight: '500',
                      fontSize: '0.7rem',
                      textAlign: 'center',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {row.challanNumber || ''}
                    </td>
                    <td style={{ 
                      padding: '6px 4px',
                      color: '#64748b',
                      fontSize: '0.7rem',
                      textAlign: 'center',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {row.type || 'DEL'}
                    </td>
                    <td style={{ 
                      padding: '6px 4px',
                      color: '#64748b',
                      fontSize: '0.7rem',
                      textAlign: 'center',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {firstItem?.name || ''}
                    </td>
                    <td style={{ 
                      padding: '6px 4px',
                      color: '#1e293b',
                      fontSize: '0.7rem',
                      textAlign: 'center',
                      fontWeight: '500',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {totalDelivered || totalReceived || '0'}
                    </td>
                    <td style={{ 
                      padding: '6px 4px',
                      color: '#64748b',
                      fontSize: '0.7rem',
                      textAlign: 'center',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {row.siteLocation || 'Main Site'}
                    </td>
                    <td style={{ 
                      padding: '6px 4px',
                      color: lastItemBalance < 0 ? '#dc2626' : '#16a34a',
                      fontSize: '0.7rem',
                      textAlign: 'center',
                      fontWeight: '600',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {Math.abs(lastItemBalance || 0).toFixed(2)}
                    </td>
                    <td style={{ 
                      padding: '6px 4px',
                      borderBottom: '1px solid #f1f5f9',
                      whiteSpace: 'nowrap',
                      minWidth: '70px',
                      textAlign: 'center'
                    }}>
                      <div style={{ 
                        display: 'flex',
                        gap: '2px',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}>
                        <button 
                          onClick={() => handleDownloadPDF(row)}
                          title="Download PDF"
                          aria-label="Download PDF"
                          style={{
                            padding: '4px',
                            border: 'none',
                            borderRadius: '3px',
                            backgroundColor: '#dc2626',
                            color: 'white',
                            cursor: 'pointer',
                            fontSize: 'clamp(0.65rem, 2vw, 0.75rem)',
                            minWidth: window.innerWidth > 768 ? '28px' : '24px',
                            height: window.innerWidth > 768 ? '28px' : '24px',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#b91c1c';
                            e.target.style.transform = 'translateY(-1px)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#dc2626';
                            e.target.style.transform = 'translateY(0)';
                          }}
                        >
                          <FaFilePdf style={{ fontSize: '12px' }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody> 
          </table>
        )}
      </div>
      </div>

      {/* Pagination */}
      <div style={{ 
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '16px',
        marginTop: '20px',
        flexWrap: 'wrap'
      }}>
        <button 
          disabled={page === 0} 
          onClick={() => setPage(page - 1)}
          style={{
            padding: '8px 16px',
            border: '1px solid #e2e8f0',
            borderRadius: '6px',
            backgroundColor: page === 0 ? '#f8fafc' : '#ffffff',
            color: page === 0 ? '#9ca3af' : '#374151',
            fontSize: '14px',
            fontWeight: '500',
            cursor: page === 0 ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            outline: 'none'
          }}
          onMouseEnter={(e) => {
            if (page !== 0) {
              e.target.style.backgroundColor = '#f8fafc';
              e.target.style.borderColor = '#3b82f6';
              e.target.style.color = '#3b82f6';
            }
          }}
          onMouseLeave={(e) => {
            if (page !== 0) {
              e.target.style.backgroundColor = '#ffffff';
              e.target.style.borderColor = '#e2e8f0';
              e.target.style.color = '#374151';
            }
          }}
        >
          Previous
        </button>
        <span style={{ 
          color: '#64748b',
          fontSize: '14px',
          fontWeight: '500'
        }}>
          Page {page + 1} of {totalPages}
        </span>
        <button 
          disabled={page === totalPages - 1} 
          onClick={() => setPage(page + 1)}
          style={{
            padding: '8px 16px',
            border: '1px solid #e2e8f0',
            borderRadius: '6px',
            backgroundColor: page === totalPages - 1 ? '#f8fafc' : '#ffffff',
            color: page === totalPages - 1 ? '#9ca3af' : '#374151',
            fontSize: '14px',
            fontWeight: '500',
            cursor: page === totalPages - 1 ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            outline: 'none'
          }}
          onMouseEnter={(e) => {
            if (page !== totalPages - 1) {
              e.target.style.backgroundColor = '#f8fafc';
              e.target.style.borderColor = '#3b82f6';
              e.target.style.color = '#3b82f6';
            }
          }}
          onMouseLeave={(e) => {
            if (page !== totalPages - 1) {
              e.target.style.backgroundColor = '#ffffff';
              e.target.style.borderColor = '#e2e8f0';
              e.target.style.color = '#374151';
            }
          }}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default ViewPassbook;
